/**
 * User Form Component
 * ===================
 * Form for creating and editing users
 */

import React, { useState, useEffect, useRef } from 'react';
import RoleSelector from './RoleSelector';
import { calculatePasswordStrength } from './PasswordStrengthIndicator';

const UserForm = ({
  user = null,
  onSubmit,
  onCancel,
  loading = false,
  organizations = [],
  defaultOrgId = null,
  isSuperAdmin = false,
}) => {
  const isEditMode = !!user;

  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    department: '',
    phone: '',
    roles: [],
    primary_role: '',
    password: '',
    confirm_password: '',
    organization_id: defaultOrgId || '',
    organization_ids: defaultOrgId ? [defaultOrgId] : [],
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    if (user) {
      // A user has a single role. Collapse any legacy multi-role data to one,
      // preferring the stored primary_role.
      const existingRoles = (user.roles || []).map((r) => (typeof r === 'string' ? r : r.role));
      const singleRole = user.primary_role || existingRoles[0] || '';
      setFormData({
        email: user.email || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        department: user.department || '',
        phone: user.phone || '',
        roles: singleRole ? [singleRole] : [],
        primary_role: singleRole,
        password: '',
        confirm_password: '',
        organization_id: defaultOrgId || '',
        organization_ids: (user.organizations || []).map((o) => o.id),
      });
    }
  }, [user, defaultOrgId]);

  const handleOrgToggle = (orgId) => {
    setFormData((prev) => {
      const selected = prev.organization_ids.includes(orgId)
        ? prev.organization_ids.filter((id) => id !== orgId)
        : [...prev.organization_ids, orgId];
      return { ...prev, organization_ids: selected };
    });
    if (errors.organization_ids) {
      setErrors((prev) => ({ ...prev, organization_ids: null }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleRolesChange = (roles) => {
    setFormData((prev) => ({ ...prev, roles }));
    if (errors.roles) {
      setErrors((prev) => ({ ...prev, roles: null }));
    }
  };

  const handlePrimaryRoleChange = (primaryRole) => {
    setFormData((prev) => ({ ...prev, primary_role: primaryRole }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (formData.roles.length === 0) {
      newErrors.roles = 'A role is required';
    }

    if (isSuperAdmin && formData.organization_ids.length === 0) {
      newErrors.organization_ids = 'Select at least one organization';
    }

    // Non-super admins pick a single organization at create time; required.
    if (!isSuperAdmin && !isEditMode && !formData.organization_id) {
      newErrors.organization_id = 'Organization is required';
    }

    // E.164 phone number validation (must match backend: ^\+[1-9]\d{1,14}$)
    if (formData.phone) {
      // No separators allowed — leading '+', a non-zero digit, then 1-14 more digits
      if (!/^\+[1-9]\d{1,14}$/.test(formData.phone)) {
        newErrors.phone = 'Please enter a valid phone number with country code (e.g., +14155552671)';
      }
    }

    // Password validation (only in create mode and if password is entered)
    if (!isEditMode && formData.password) {
      const { strength, passed } = calculatePasswordStrength(formData.password);

      // Check if all requirements are met
      if (passed.length < 5) {
        newErrors.password = 'Password does not meet all requirements';
      }

      // Check password confirmation
      if (formData.password !== formData.confirm_password) {
        newErrors.confirm_password = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return newErrors;
  };

  // Scroll the modal to the first field with an error and focus it, so the user
  // isn't left guessing why the submit button "isn't working".
  const focusFirstError = (errs) => {
    // Order matches the visual top-to-bottom layout of the form
    const order = [
      'email',
      'first_name',
      'last_name',
      'phone',
      'organization_ids',
      'organization_id',
      'password',
      'confirm_password',
      'roles',
    ];
    const firstKey = order.find((key) => errs[key]);
    if (!firstKey || !formRef.current) return;

    const anchor = formRef.current.querySelector(`[data-field="${firstKey}"]`);
    if (!anchor) return;

    anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const focusable = anchor.querySelector('input, select, textarea');
    if (focusable) {
      // Delay focus so the smooth scroll isn't interrupted by focus jumping
      setTimeout(() => focusable.focus({ preventScroll: true }), 300);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length === 0) {
      // Prepare submission data
      const submitData = { ...formData };

      // Only include password if in create mode and it's provided
      if (isEditMode || !submitData.password) {
        delete submitData.password;
      }

      // Remove confirm_password (not needed by API)
      delete submitData.confirm_password;

      // Super admins assign via organization_ids (multi-org); everyone else uses
      // the single organization_id (create only).
      if (isSuperAdmin) {
        delete submitData.organization_id;
      } else {
        delete submitData.organization_ids;
      }

      onSubmit(submitData);
    } else {
      focusFirstError(validationErrors);
    }
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
      {/* Email */}
      <div data-field="email">
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email Address <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          disabled={isEditMode || loading}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-0 focus:border-primary ${
            errors.email ? 'border-red-500' : 'border-gray-300'
          } ${isEditMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          placeholder="user@zcortexai.com"
        />
        {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
        {isEditMode && (
          <p className="mt-1 text-sm text-gray-500">Email cannot be changed</p>
        )}
      </div>

      {/* Name Fields */}
      <div className="grid grid-cols-2 gap-4">
        <div data-field="first_name">
          <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
            First Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="first_name"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            disabled={loading}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-0 focus:border-primary ${
              errors.first_name ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="John"
          />
          {errors.first_name && <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>}
        </div>

        <div data-field="last_name">
          <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
            Last Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="last_name"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            disabled={loading}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-0 focus:border-primary ${
              errors.last_name ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Doe"
          />
          {errors.last_name && <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>}
        </div>
      </div>

      {/* Department */}
      <div>
        <label htmlFor="department" className="block text-sm font-medium text-gray-700 mb-1">
          Department
        </label>
        <input
          type="text"
          id="department"
          name="department"
          value={formData.department}
          onChange={handleChange}
          disabled={loading}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-0 focus:border-primary"
          placeholder="Business Development"
        />
      </div>

      {/* Phone Number */}
      <div data-field="phone">
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
          Phone Number
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          disabled={loading}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-0 focus:border-primary ${
            errors.phone ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="+14155552671"
        />
        {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
        <p className="mt-1 text-xs text-gray-500">
          Enter phone number in international format with country code, no spaces or dashes (e.g., +14155552671)
        </p>
      </div>

      {/* Organizations — multi-select for super admins (create + edit) */}
      {isSuperAdmin ? (
        <div data-field="organization_ids">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Organizations <span className="text-red-500">*</span>
          </label>
          <div
            className={`max-h-48 overflow-y-auto border rounded-lg divide-y divide-gray-100 ${
              errors.organization_ids ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            {organizations.length === 0 ? (
              <p className="px-4 py-3 text-sm text-gray-500">No organizations available</p>
            ) : (
              organizations.map((org) => (
                <label
                  key={org.id}
                  className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={formData.organization_ids.includes(org.id)}
                    onChange={() => handleOrgToggle(org.id)}
                    disabled={loading}
                    className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-0"
                  />
                  <span className="text-sm text-gray-900">{org.name}</span>
                </label>
              ))
            )}
          </div>
          {errors.organization_ids && (
            <p className="mt-1 text-sm text-red-600">{errors.organization_ids}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            The user is assigned to each selected organization with their primary role.
          </p>
        </div>
      ) : (
        /* Single organization — non-super admins, create mode only */
        !isEditMode && (
          <div data-field="organization_id">
            <label htmlFor="organization_id" className="block text-sm font-medium text-gray-700 mb-1">
              Organization <span className="text-red-500">*</span>
            </label>
            <select
              id="organization_id"
              name="organization_id"
              value={formData.organization_id}
              onChange={handleChange}
              disabled={loading || organizations.length <= 1}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-0 focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed ${errors.organization_id ? 'border-red-500' : 'border-gray-300'}`}
            >
              <option value="">Select organization</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
            {errors.organization_id && (
              <p className="mt-1 text-sm text-red-600">{errors.organization_id}</p>
            )}
            {organizations.length <= 1 && (
              <p className="mt-1 text-xs text-gray-500">User will be added to your organization.</p>
            )}
          </div>
        )
      )}

      {/* Password Section - Only in Create Mode */}
      {!isEditMode && (
        <div className="space-y-4 border-t border-gray-200 pt-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-900">
              Set Password
            </h3>
          </div>

          <p className="text-sm text-gray-600">
            Leave password empty to auto-generate a secure password.
          </p>

          {/* Password Input */}
          <div data-field="password">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                disabled={loading}
                className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-0 focus:border-primary ${
                  errors.password ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter a strong password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 px-3 flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
            <p className="mt-1 text-xs text-gray-500">
              Password must be at least 12 characters with uppercase, lowercase, number, and special character.
            </p>
          </div>

          {/* Confirm Password */}
          <div data-field="confirm_password">
            <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirm_password"
                name="confirm_password"
                value={formData.confirm_password}
                onChange={handleChange}
                disabled={loading}
                className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-0 focus:border-primary ${
                  errors.confirm_password ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Re-enter your password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 px-3 flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
              >
                {showConfirmPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            {errors.confirm_password && <p className="mt-1 text-sm text-red-600">{errors.confirm_password}</p>}
          </div>
        </div>
      )}

      {/* Role Selection */}
      <div data-field="roles">
        <RoleSelector
          selectedRoles={formData.roles}
          primaryRole={formData.primary_role}
          onChange={handleRolesChange}
          onPrimaryRoleChange={handlePrimaryRoleChange}
          disabled={loading}
        />
        {errors.roles && <p className="mt-2 text-sm text-red-600">{errors.roles}</p>}
      </div>

      {/* Form Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors shadow-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {loading && (
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          )}
          {isEditMode ? 'Update User' : 'Create User'}
        </button>
      </div>
    </form>
  );
};

export default UserForm;
